import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "../../../../context/useGlobalState";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { showMissingInfoByTrackId, getTrackLanguages } from "../../../../api";
import { getZonesAndRooms } from "../../../../api/museum";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import Button from "../../../../components/ui/Button";
import stepStyles from "../createStep.module.css";

interface MissingInfoItem {
    id: string | number;
    internalName: string;
    position: number;
    [key: string]: any;
}

interface MissingTrackInfoItem {
    name?: boolean;
    subtitle?: boolean;
    allData?: boolean;
    blocking: boolean;
}

interface MissingInfoLanguage {
    language: string;
    missingInfo: MissingInfoItem[];
    missingTrackInfo?: MissingTrackInfoItem | null;
}

interface StoryWithoutRoom {
    id: string;
    name: string;
    position: number;
    floorLabel: string;
}

interface IncompleteDataProps {
    navigateToContinue?: () => void;
}

const IncompleteData: React.FC<IncompleteDataProps> = ({ navigateToContinue }) => {
    const { data } = useGlobalState();

    const { trackData } = useTrackCreation();
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [existingNonLanguages, setExistingNonLanguages] = useState<MissingInfoLanguage[]>([]);
    const [missingInfo, setMissingInfo] = useState<MissingInfoLanguage[]>([]);
    const [storiesWithoutRoom, setStoriesWithoutRoom] = useState<StoryWithoutRoom[]>([]);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const checkMissingData = async () => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!trackId || !token) return;
        const missingLanguages = await showMissingInfoByTrackId({ token, trackId });
        setMissingInfo(missingLanguages.checkMissingInfoPerLanguage);
        setExistingNonLanguages(missingLanguages.checkMissingInfoPerNonActiveLanguage);
    };

    const checkStoriesWithoutRoom = async () => {
        // Museum-only check — do not call getZonesAndRooms on other track types
        if (trackData.type !== "iduniquemusee") {
            setStoriesWithoutRoom([]);
            return;
        }

        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!trackId || !token) return;

        try {
            const response = await getZonesAndRooms({ token, trackId });
            if (!response?.subPlans) {
                setStoriesWithoutRoom([]);
                return;
            }

            const zones = response.subPlansZones || [];
            const museumHasAnyRoom = zones.length > 0;
            if (!museumHasAnyRoom) {
                setStoriesWithoutRoom([]);
                return;
            }

            const orphans: StoryWithoutRoom[] = [];

            for (const floor of response.subPlans) {
                const floorLabel = floor.internalName?.trim() || `${t("floorNumber")} ${floor.floorNumber}`;

                for (const story of floor.clickableZones || []) {
                    if (story.subPlansZonesId) continue;
                    orphans.push({
                        id: story.id,
                        name: story.internalName?.trim() || t("unnamed"),
                        position: story.position,
                        floorLabel,
                    });
                }
            }

            setStoriesWithoutRoom(orphans);
        } catch {
            setStoriesWithoutRoom([]);
        }
    };

    useEffect(() => {
        if (trackData.trackId) {
            checkMissingData();
            checkStoriesWithoutRoom();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, trackData.type, existingLanguages]);

    const getTrackLanguagesAPI = async () => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!trackId || !token) return;
        const response = await getTrackLanguages({ token, id: trackId });
        const languages: string[] = response.formattedLanguages || [];
        const arrayLang: string[] = [];
        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            const languageName = data.languagesAI.find((lang) => lang.value === language);
            if (languageName) {
                arrayLang.push(languageName.label);
            }
        }
        setExistingLanguages(arrayLang);
    };

    useEffect(() => {
        if (trackData.trackId && trackData.trackId !== "") {
            getTrackLanguagesAPI();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, data]);

    interface MissingInfoTableProps {
        missingInfoData: MissingInfoLanguage[];
    }

    const MissingInfoTable: React.FC<MissingInfoTableProps> = ({ missingInfoData }) => {
        const rows = missingInfoData.flatMap((languageInfo) =>
            languageInfo.missingInfo.map((info, index) => ({
                key: `${languageInfo.language}-${info.id}-${index}`,
                language: languageInfo.language,
                name: info.internalName,
                position: info.position,
                details: Object.keys(info)
                    .filter((key) => key !== "id" && key !== "internalName" && key !== "position")
                    .join(", "),
            })),
        );

        return (
            <div className={stepStyles.incompleteTableWrap}>
                <table className={stepStyles.incompleteTable}>
                    <thead>
                        <tr>
                            <th className={stepStyles.incompleteTh} style={{ width: "20%" }}>
                                {t("language")}
                            </th>
                            <th className={stepStyles.incompleteTh} style={{ width: "22%" }}>
                                {t("name")}
                            </th>
                            <th className={stepStyles.incompleteTh} style={{ width: "18%" }}>
                                {t("position")}
                            </th>
                            <th className={stepStyles.incompleteTh}>{t("missingDetails")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr className={stepStyles.incompleteEmpty}>
                                <td className={stepStyles.incompleteEmptyTd} colSpan={4}>
                                    {t("noMissingInformation", {
                                        defaultValue: "Aucune information manquante.",
                                    })}
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.key}>
                                    <td className={stepStyles.incompleteTd}>{row.language}</td>
                                    <td className={stepStyles.incompleteTd}>{row.name}</td>
                                    <td className={stepStyles.incompleteTd}>{row.position}</td>
                                    <td className={stepStyles.incompleteTd}>{row.details}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // The track title is what the app resolves to display the product: when it is missing,
    // the product is not just incomplete, it is invisible in that language. Reported first,
    // and apart from the per-story tables, because nothing below it matters until it is fixed.
    const missingTrackInfoRows = missingInfo
        .filter((languageInfo) => languageInfo.missingTrackInfo)
        .map((languageInfo) => {
            const trackInfo = languageInfo.missingTrackInfo as MissingTrackInfoItem;
            const details = trackInfo.allData
                ? t("missingTrackTitleAllData")
                : [trackInfo.name ? t("name") : null, trackInfo.subtitle ? t("subtitle") : null]
                      .filter(Boolean)
                      .join(", ");

            return { language: languageInfo.language, details, blocking: trackInfo.blocking };
        });

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${stepStyles.stepIncomplete}`}>
            <TitleSubtitle
                title={t("incompleteData")}
                subtitle={t("missingInfoToMakeYourTourAvailable")}
                layout="incomplete"
            />

            {missingTrackInfoRows.length > 0 && (
                <div className={`experience-incomplete-block ${stepStyles.incompleteBlock}`}>
                    <h3 className={`experience-incomplete-h ${stepStyles.incompleteHeading}`}>
                        {t("missingTrackTitleTitle")}
                    </h3>
                    <p className={stepStyles.incompleteHint}>{t("missingTrackTitleDescription")}</p>
                    <div className={stepStyles.incompleteTableWrap}>
                        <table className={stepStyles.incompleteTable}>
                            <thead>
                                <tr>
                                    <th className={stepStyles.incompleteTh} style={{ width: "20%" }}>
                                        {t("language")}
                                    </th>
                                    <th className={stepStyles.incompleteTh}>{t("missingDetails")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {missingTrackInfoRows.map((row) => (
                                    <tr key={row.language}>
                                        <td className={stepStyles.incompleteTd}>{row.language}</td>
                                        <td className={stepStyles.incompleteTd}>
                                            {row.details}
                                            {row.blocking ? ` — ${t("missingTrackTitleBlocking")}` : ""}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {storiesWithoutRoom.length > 0 && (
                <div className={`experience-incomplete-block ${stepStyles.incompleteBlock}`}>
                    <h3 className={`experience-incomplete-h ${stepStyles.incompleteHeading}`}>
                        {t("storiesWithoutRoomTitle")}
                    </h3>
                    <p className={stepStyles.incompleteHint}>{t("storiesWithoutRoomDescription")}</p>
                    <div className={stepStyles.incompleteTableWrap}>
                        <table className={stepStyles.incompleteTable}>
                            <thead>
                                <tr>
                                    <th className={stepStyles.incompleteTh} style={{ width: "28%" }}>
                                        {t("name")}
                                    </th>
                                    <th className={stepStyles.incompleteTh} style={{ width: "18%" }}>
                                        {t("position")}
                                    </th>
                                    <th className={stepStyles.incompleteTh} style={{ width: "26%" }}>
                                        {t("floorNumber")}
                                    </th>
                                    <th className={stepStyles.incompleteTh}>{t("missingDetails")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {storiesWithoutRoom.map((story) => (
                                    <tr key={story.id}>
                                        <td className={stepStyles.incompleteTd}>{story.name}</td>
                                        <td className={stepStyles.incompleteTd}>{story.position}</td>
                                        <td className={stepStyles.incompleteTd}>{story.floorLabel}</td>
                                        <td className={stepStyles.incompleteTd}>{t("missingRoomAssignment")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className={`experience-incomplete-block ${stepStyles.incompleteBlock}`}>
                <h3 className={`experience-incomplete-h ${stepStyles.incompleteHeading}`}>
                    {t("missingInfoOnPublishedLanguages")}
                </h3>
                <MissingInfoTable missingInfoData={missingInfo} />
            </div>

            <div className={`experience-incomplete-block ${stepStyles.incompleteBlock}`}>
                <h3 className={`experience-incomplete-h ${stepStyles.incompleteHeading}`}>
                    {t("missingInfoOnUnpublishedLanguages")}
                </h3>
                <MissingInfoTable missingInfoData={existingNonLanguages} />
            </div>

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={stepStyles.continueBtn}
                    />
                </div>
            )}
        </div>
    );
};

export default IncompleteData;
